import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useForm, FormProvider } from "react-hook-form";
import { Button, Grid, Tabs, Tab } from "@material-ui/core";
import * as dayjs from "dayjs";
import { setLoader } from "actions/Base";
import CardBox from "components/CardBox/index";
import FormSelectAutoComplete from "components/Form/Controls/select-autocomplete";
import FormDatePicker from "components/Form/Controls/datepicker";
import Table, { defaultCol, getTextLabels } from "components/Form/Table";
import IntlMessages, { IntlGetText } from "util/IntlMessages";
import {
  callApi,
  getPatientFollowUpOptions,
  postPatientFollowUpList,
  postPatientFollowUpSummaries,
} from "api/apiList";
import FollowUpTable from "./FollowUpTable";
import LastImpactViewModal from "./LastImpactViewModal";

const defaultValues = {
  productLine: null,
  product: null,
  stateParent: null,
  state: null,
  ods: null,
  insurance: null,
  marketChain: null,
  marketChainDelivery: null,
  city: null,
  estimatedFrom: null,
  estimatedTo: null,
  entryFrom: null,
  entryTo: null,
};

const idOf = (value) =>
  value && value.value !== undefined && value.value !== "" ? value.value : null;

const toDate = (value) => {
  if (!value) return null;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : null;
};

const PENDING_STATE_PARENT = "PENDIENTE";

const formatStateParentName = (name) =>
  name != null && String(name).trim() ? String(name).trim() : PENDING_STATE_PARENT;

const normalizeCountItem = (item) => ({
  ...item,
  id: item.id ?? item.Id,
  name: formatStateParentName(item.name ?? item.Name),
  count: item.count ?? item.Count ?? 0,
});

const normalizeSummaries = (data) => {
  if (!data) {
    return {
      total: 0,
      byStateParent: [],
      byMedicine: [],
      byOds: null,
    };
  }

  const byOds = data.byOds ?? data.ByOds;
  return {
    total: data.total ?? data.Total ?? 0,
    byStateParent: (data.byStateParent ?? data.ByStateParent ?? []).map(normalizeCountItem),
    byMedicine: data.byMedicine ?? data.ByMedicine ?? [],
    byOds: byOds
      ? byOds.map((row) => ({
          ...row,
          ods: row.ods ?? row.Ods,
          total: row.total ?? row.Total ?? 0,
          byStateParent: (row.byStateParent ?? row.ByStateParent ?? []).map(normalizeCountItem),
        }))
      : null,
  };
};

const normalizeRows = (rows) =>
  (rows || []).map((row) => ({
    ...row,
    personId: row.personId ?? row.PersonId,
    stateParent: formatStateParentName(row.stateParent ?? row.StateParent),
  }));

const buildFilter = (values, search) => ({
  productLineId: idOf(values.productLine),
  productId: idOf(values.product),
  stateParentId: idOf(values.stateParent),
  stateId: idOf(values.state),
  ods: idOf(values.ods),
  insuranceId: idOf(values.insurance),
  marketChainId: idOf(values.marketChain),
  marketChainDeliveryId: idOf(values.marketChainDelivery),
  estimatedDeliveryDateFrom: toDate(values.estimatedFrom),
  estimatedDeliveryDateTo: toDate(values.estimatedTo),
  creationDateFrom: toDate(values.entryFrom),
  creationDateTo: toDate(values.entryTo),
  cityId: idOf(values.city),
  search: search || null,
});

const PatientFollowUp = () => {
  const dispatch = useDispatch();
  const fnSetLoader = (data) => dispatch(setLoader(data));
  const methods = useForm({ defaultValues });
  const { watch, reset, setValue } = methods;
  const values = watch();

  const [options, setOptions] = useState({
    showOds: false,
    showAlternateCode: false,
    productLines: [],
    products: [],
    insurances: [],
    marketChains: [],
    cities: [],
    ods: [],
    parentStates: [],
    states: [],
  });
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [summaries, setSummaries] = useState({
    total: 0,
    byStateParent: [],
    byMedicine: [],
    byOds: null,
  });
  const [tab, setTab] = useState(0);
  const [impactView, setImpactView] = useState({ open: false, personId: null, label: "" });
  const prevFilter = useRef("");

  const filter = buildFilter(values, search);
  const filterKey = JSON.stringify(filter);

  const stateOptions = (options.states || []).filter((s) => {
    const parentId = idOf(values.stateParent);
    if (parentId == null) return true;
    return s.parentId === parentId;
  });

  useEffect(() => {
    callApi(getPatientFollowUpOptions, setOptions, fnSetLoader);
  }, []);

  useEffect(() => {
    if (prevFilter.current !== filterKey) {
      prevFilter.current = filterKey;
      if (page !== 0) {
        setPage(0);
        return;
      }
    }

    callApi(
      () =>
        postPatientFollowUpList({
          page,
          rowsPerPage,
          filter,
        }),
      (data) => {
        setCount(data.count ?? data.Count ?? 0);
        setRows(normalizeRows(data.data ?? data.Data));
      },
      fnSetLoader
    );

    callApi(
      () => postPatientFollowUpSummaries(filter),
      (data) => setSummaries(normalizeSummaries(data)),
      fnSetLoader
    );
  }, [page, rowsPerPage, filterKey]);

  const onTableChange = (action, tableState) => {
    if (action === "changePage") {
      setPage(tableState.page);
    }
    if (action === "changeRowsPerPage") {
      setRowsPerPage(tableState.rowsPerPage);
      setPage(0);
    }
    if (action === "search") {
      setSearch(tableState.searchText || "");
    }
  };

  const clearFilters = () => {
    reset(defaultValues);
    setSearch("");
    setPage(0);
  };

  const openLastImpactView = (row) => {
    const personId = row.personId ?? row.PersonId;
    if (!personId) return;
    const label = [row.code, row.firstName, row.lastName].filter(Boolean).join(" - ");
    setImpactView({ open: true, personId, label });
  };

  const closeLastImpactView = () => {
    setImpactView({ open: false, personId: null, label: "" });
  };

  const parentNames = [
    ...new Set([
      ...(options.parentStates || []).map((p) => p.name),
      ...(summaries.byStateParent || []).map((x) => x.name),
    ]),
  ].sort();
  const odsColumns = [
    defaultCol("ods", "core.ods"),
    ...parentNames.map((name) => ({
      name: name,
      label: name,
      options: { filter: false, sort: false },
    })),
    {
      name: "total",
      label: IntlGetText("patientFollowUp.total"),
      options: { filter: false, sort: false },
    },
  ];

  const odsData = (summaries.byOds || []).map((row) => {
    const item = { ods: row.ods, total: row.total };
    parentNames.forEach((name) => {
      const found = (row.byStateParent || []).find((x) => x.name === name);
      item[name] = found ? found.count : 0;
    });
    return item;
  });

  return (
    <div className="app-wrapper">
      <FormProvider {...methods}>
        <CardBox
          styleName="col-12"
          heading={<IntlMessages id="patientFollowUp.title" />}
        >
          <IntlMessages id="patientFollowUp.filters" />
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <FormSelectAutoComplete
                name="productLine"
                label={<IntlMessages id="core.productLine" />}
                options={options.productLines}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormSelectAutoComplete
                name="product"
                label={<IntlMessages id="core.product" />}
                options={options.products}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormSelectAutoComplete
                name="stateParent"
                label={<IntlMessages id="patientFollowUp.stateParent" />}
                options={options.parentStates}
                margin="normal"
                onChange={() => setValue("state", null)}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormSelectAutoComplete
                name="state"
                label={<IntlMessages id="patientFollowUp.state" />}
                options={stateOptions}
                margin="normal"
              />
            </Grid>
            {options.showOds && (
              <Grid item xs={12} md={3}>
                <FormSelectAutoComplete
                  name="ods"
                  label={<IntlMessages id="core.ods" />}
                  options={options.ods}
                  margin="normal"
                />
              </Grid>
            )}
            <Grid item xs={12} md={3}>
              <FormSelectAutoComplete
                name="insurance"
                label={<IntlMessages id="core.insurance" />}
                options={options.insurances}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormSelectAutoComplete
                name="marketChain"
                label={<IntlMessages id="patientFollowUp.olCompra" />}
                options={options.marketChains}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormSelectAutoComplete
                name="marketChainDelivery"
                label={<IntlMessages id="patientFollowUp.olEntrega" />}
                options={options.marketChains}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormSelectAutoComplete
                name="city"
                label={<IntlMessages id="core.city" />}
                options={options.cities}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormDatePicker
                name="estimatedFrom"
                label={<IntlMessages id="patientFollowUp.estimatedFrom" />}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormDatePicker
                name="estimatedTo"
                label={<IntlMessages id="patientFollowUp.estimatedTo" />}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormDatePicker
                name="entryFrom"
                label={<IntlMessages id="patientFollowUp.entryFrom" />}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormDatePicker
                name="entryTo"
                label={<IntlMessages id="patientFollowUp.entryTo" />}
              />
            </Grid>
            <Grid item xs={12}>
              <Button variant="outlined" color="primary" onClick={clearFilters}>
                <IntlMessages id="patientFollowUp.clearFilters" />
              </Button>
            </Grid>
          </Grid>
        </CardBox>
      </FormProvider>

      <CardBox
        styleName="col-12"
        heading={<IntlMessages id="patientFollowUp.summaries" />}
      >
        <div className="d-flex flex-wrap">
          {(summaries.byStateParent || []).map((item) => (
            <div
              key={item.id || item.name}
              className="badge text-uppercase text-white bg-teal mr-2 mb-2"
              style={{ fontSize: 13, padding: "8px 12px" }}
            >
              {formatStateParentName(item.name)}: {item.count}
            </div>
          ))}
          <div
            className="badge text-uppercase text-white bg-grey mr-2 mb-2"
            style={{ fontSize: 13, padding: "8px 12px" }}
          >
            {IntlGetText("patientFollowUp.total")}: {summaries.total || 0}
          </div>
        </div>
      </CardBox>

      <div className="col-12">
        <div className="jr-card">
          <Tabs
            value={tab}
            onChange={(e, value) => setTab(value)}
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab label={IntlGetText("patientFollowUp.detail")} />
            <Tab label={IntlGetText("patientFollowUp.byMedicine")} />
            {options.showOds && (
              <Tab label={IntlGetText("patientFollowUp.byOds")} />
            )}
          </Tabs>
          <div className="mt-3">
            {tab === 0 && (
              <FollowUpTable
                data={rows}
                count={count}
                page={page}
                rowsPerPage={rowsPerPage}
                showOds={options.showOds}
                showAlternateCode={options.showAlternateCode}
                onTableChange={onTableChange}
                onPatientClick={openLastImpactView}
              />
            )}
            {tab === 1 && (
              <Table
                title={IntlGetText("patientFollowUp.byMedicine")}
                data={summaries.byMedicine || []}
                columns={[
                  defaultCol("medicine", "patientFollowUp.medicine"),
                  defaultCol("code", "core.code"),
                  defaultCol("patientCount", "patientFollowUp.patientCount"),
                ]}
                options={{
                  ...{
                    filter: false,
                    download: false,
                    print: false,
                    selectableRows: "none",
                    textLabels: getTextLabels(),
                  },
                }}
              />
            )}
            {options.showOds && tab === 2 && (
              <Table
                title={IntlGetText("patientFollowUp.byOds")}
                data={odsData}
                columns={odsColumns}
                options={{
                  filter: false,
                  download: false,
                  print: false,
                  selectableRows: "none",
                  textLabels: getTextLabels(),
                }}
              />
            )}
          </div>
        </div>
      </div>
      <LastImpactViewModal
        open={impactView.open}
        personId={impactView.personId}
        patientLabel={impactView.label}
        onClose={closeLastImpactView}
      />
    </div>
  );
};

export default PatientFollowUp;
